import { useEffect, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Line, ComposedChart, Legend } from "recharts";
import { TrendingUp, Award } from "lucide-react";
import api from "@/lib/api";

const MONTH_LABELS = ["jan", "fév", "mar", "avr", "mai", "juin", "juil", "août", "sep", "oct", "nov", "déc"];

function formatMonth(ym) {
  if (!ym) return "";
  const [y, m] = ym.split("-");
  const idx = Math.max(0, Math.min(11, parseInt(m, 10) - 1));
  return `${MONTH_LABELS[idx]} ${y.slice(2)}`;
}

function formatXOF(n) {
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n || 0) + " XOF";
}

export default function MonthlyRevenueChart() {
  const [data, setData] = useState({ rows: [], best: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/stats/monthly?months=12").then(({ data }) => setData(data || { rows: [] })).catch(() => setData({ rows: [] })).finally(() => setLoading(false));
  }, []);

  const rows = (data.rows || []).map((r) => ({
    ...r,
    label: formatMonth(r.month),
    volume_xaf: Math.round(r.volume_xaf || 0),
    fees_xaf: Math.round(r.fees_xaf || 0),
  }));

  const totalVolume = rows.reduce((s, r) => s + r.volume_xaf, 0);
  const totalFees = rows.reduce((s, r) => s + r.fees_xaf, 0);
  const totalCount = rows.reduce((s, r) => s + r.count, 0);

  return (
    <div className="sl-card" data-testid="monthly-revenue-chart">
      <div className="flex items-center gap-2.5">
        <span className="w-9 h-9 rounded-lg bg-emerald-50 text-emerald-700 grid place-items-center flex-shrink-0"><TrendingUp size={16} /></span>
        <div className="min-w-0">
          <h3 className="font-heading text-lg font-bold leading-tight">Chiffre d&apos;affaires par mois</h3>
          <p className="text-xs text-gray-500">Évolution sur les 12 derniers mois (transactions non annulées).</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-3 gap-2 sm:gap-3">
        <div className="rounded-xl border border-gray-100 bg-[var(--sl-bg-alt)] px-3 py-2.5">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Volume total</div>
          <div className="font-heading text-base sm:text-xl font-bold text-[var(--sl-primary)] truncate mt-0.5" data-testid="monthly-total-volume">{formatXOF(totalVolume)}</div>
        </div>
        <div className="rounded-xl border border-gray-100 bg-[var(--sl-bg-alt)] px-3 py-2.5">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Frais perçus</div>
          <div className="font-heading text-base sm:text-xl font-bold text-[var(--sl-secondary-dark)] truncate mt-0.5" data-testid="monthly-total-fees">{formatXOF(totalFees)}</div>
        </div>
        <div className="rounded-xl border border-gray-100 bg-[var(--sl-bg-alt)] px-3 py-2.5">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-semibold">Transactions</div>
          <div className="font-heading text-base sm:text-xl font-bold text-gray-900 truncate mt-0.5" data-testid="monthly-total-count">{totalCount}</div>
        </div>
      </div>

      {data.best && (
        <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-800 text-xs font-semibold" data-testid="monthly-best-month">
          <Award size={13} />
          Meilleur mois : <span className="font-bold">{formatMonth(data.best.month)}</span> — {formatXOF(data.best.volume_xaf)} sur {data.best.count} tx
        </div>
      )}

      <div className="mt-5 h-72">
        {loading ? (
          <div className="h-full grid place-items-center text-gray-500 text-sm">Chargement…</div>
        ) : rows.length === 0 ? (
          <div className="h-full grid place-items-center text-gray-500 text-sm">Aucune transaction sur la période.</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 10, right: 12, left: 0, bottom: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} stroke="#9ca3af" />
              <YAxis tick={{ fontSize: 11 }} stroke="#9ca3af" tickFormatter={(v) => v >= 1e6 ? `${(v / 1e6).toFixed(1)} M` : v >= 1e3 ? `${(v / 1e3).toFixed(0)} k` : v} />
              <Tooltip
                contentStyle={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, boxShadow: "0 8px 24px -12px rgba(0,0,0,0.15)" }}
                formatter={(value, name) => [formatXOF(value), name === "volume_xaf" ? "Volume" : name === "fees_xaf" ? "Frais" : name]}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} formatter={(v) => v === "volume_xaf" ? "Volume" : v === "fees_xaf" ? "Frais" : "Nombre tx"} />
              <Bar dataKey="volume_xaf" fill="#0a4d3c" radius={[6, 6, 0, 0]} maxBarSize={48} />
              <Line type="monotone" dataKey="fees_xaf" stroke="#E89B1A" strokeWidth={2.5} dot={{ r: 3, fill: "#E89B1A" }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
